// src/pages/TermsOfService.jsx
import React from "react";
import { useNavigate, Link } from "react-router-dom";
import "../static/TermsOfService.css";
import Footer from "../components/common/Footer";
import SITE from "../config/site";

const TermsOfService = () => {
  const navigate = useNavigate();
  const lastUpdated = "January 1, 2025";

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <div>
      <div className="terms-page">
        <div className="terms-container">
          <div className="terms-header">
            <button
              className="terms-back-button"
              onClick={handleBack}
              type="button"
            >
              <i className="fas fa-arrow-left"></i> Back
            </button>
            <h1>Terms of Service</h1>
            <p className="terms-last-updated">Last updated: {lastUpdated}</p>
          </div>

          <div className="terms-content">
            {/* ── 1 ── */}
            <section className="terms-section">
              <h2>1. Agreement to These Terms</h2>
              <p>
                These Terms of Service ("Terms") govern your access to and use
                of the Hayducate website, applications, and services
                (collectively, the "Services"). By creating an account,
                enrolling in a course, or otherwise using the Services, you
                agree to be bound by these Terms.
              </p>
              <p>
                If you do not agree with these Terms, please do not use the
                Services. We may update these Terms from time to time. When we
                do, we will change the "Last updated" date above. Continued use
                of the Services after an update means you accept the revised
                Terms.
              </p>
            </section>

            {/* ── 2 ── */}
            <section className="terms-section">
              <h2>2. Eligibility</h2>
              <p>
                You must be at least 13 years old to create an account. If you
                are under 18, you may only use the Services with the consent
                and supervision of a parent or legal guardian.
              </p>
              <p>
                By using the Services, you confirm that the information you
                provide during registration is accurate, that you meet these
                eligibility requirements, and that you have the legal capacity
                to enter into this agreement.
              </p>
            </section>

            {/* ── 3 ── */}
            <section className="terms-section">
              <h2>3. Accounts and Course Enrollment</h2>

              <h3>3.1 Account required for enrollment</h3>
              <p>
                You must create an account to enroll in courses. Your account
                gives you access to the courses you have enrolled in, tracks
                your progress, and lets you resume learning where you left off.
                You are responsible for keeping your login credentials
                confidential and for all activity that occurs under your
                account.
              </p>

              <h3>3.2 Accurate information</h3>
              <p>
                When registering, you agree to provide accurate, current, and
                complete information about yourself and to keep that
                information up to date.
              </p>

              <h3>3.3 One account per person</h3>
              <p>
                Your account is personal to you. You may not share your login
                credentials with anyone else, and you may not create multiple
                accounts to circumvent restrictions, promotions, or pricing.
              </p>

              <h3>3.4 Access to course materials</h3>
              <p>
                Once enrolled, you receive a personal, non-transferable license
                to access the course materials through your account for as
                long as the course remains available on Hayducate, subject to
                these Terms. Course access is granted to your account only and
                may not be transferred, sold, or shared.
              </p>

              <h3>3.5 Progress tracking and learning data</h3>
              <p>
                To provide the service, we store information about your
                learning activity — such as courses you have opened, lessons
                you have completed, and quiz results. This data is used to
                show you your progress, resume lessons, and improve our
                courses. See our Privacy Policy for details on how we handle
                your data.
              </p>

              <h3>3.6 Account deletion</h3>
              <p>
                You may request deletion of your account at any time by
                contacting us at {""}
                <a href={`mailto:${SITE.contact.email}`} className="terms-link">
                  {SITE.contact.email}
                </a>
                . When your account is deleted, your personal information and
                learning progress are removed from our active systems, subject
                to any records we are required to keep by law.
              </p>
            </section>

            {/* ── 4 ── */}
            <section className="terms-section">
              <h2>4. The Services</h2>
              <p>
                Hayducate provides online educational content, including
                video lessons, written materials, quizzes, progress tracking,
                and — through our store — physical and digital products for
                purchase.
              </p>
              <p>
                We may add, change, or remove features, courses, or products
                at any time. We do not guarantee that any particular course,
                lesson, or product will remain available indefinitely.
              </p>
            </section>

            {/* ── 5 ── */}
            <section className="terms-section">
              <h2>5. Payments and Refunds</h2>

              <h3>5.1 Fees</h3>
              <p>
                Some courses and products require payment. Prices are shown at
                the point of purchase. You agree to pay the fees applicable
                to your order.
              </p>

              <h3>5.2 Refunds</h3>
              <p>
                Unless otherwise stated at the time of purchase, all fees are
                non-refundable. If you believe you are entitled to a refund,
                contact us at {""}
                <a href={`mailto:${SITE.contact.email}`} className="terms-link">
                  {SITE.contact.email}
                </a>{" "}
                and we will review your request in good faith.
              </p>

              <h3>5.3 Price changes</h3>
              <p>
                We may change our prices at any time. Price changes do not
                affect purchases that have already been completed.
              </p>
            </section>

            {/* ── 6 ── */}
            <section className="terms-section">
              <h2>6. Intellectual Property</h2>

              <h3>6.1 Our content</h3>
              <p>
                All content on the Services — including videos, text,
                graphics, logos, course materials, and software — is owned by
                Hayducate or our licensors and is protected by copyright,
                trademark, and other intellectual property laws.
              </p>

              <h3>6.2 Your license to use our content</h3>
              <p>
                Subject to your compliance with these Terms, we grant you a
                limited, non-exclusive, non-transferable license to access and
                use the content for your own personal, non-commercial learning.
              </p>

              <h3>6.3 What you may not do</h3>
              <ul className="terms-list">
                <li>Copy, record, redistribute, or resell course content</li>
                <li>Share your account or course access with others</li>
                <li>
                  Republish our content, in whole or in part, on any other
                  platform
                </li>
                <li>
                  Remove or alter any copyright, trademark, or attribution
                  notices
                </li>
                <li>
                  Use our content for commercial purposes without our written
                  permission
                </li>
              </ul>

              <h3>6.4 Content you submit</h3>
              <p>
                If you submit content such as comments, forum posts, or
                assignments, you retain ownership of that content. You grant
                us a non-exclusive, royalty-free license to store, display,
                and use it in connection with operating the Services.
              </p>
            </section>

            {/* ── 7 ── */}
            <section className="terms-section">
              <h2>7. Acceptable Use</h2>
              <p>You agree not to:</p>
              <ul className="terms-list">
                <li>
                  Use the Services for any unlawful purpose or in violation of
                  any applicable law
                </li>
                <li>
                  Harass, threaten, or harm other users, instructors, or staff
                </li>
                <li>
                  Post content that is defamatory, obscene, hateful, or
                  infringing
                </li>
                <li>Impersonate any person or entity</li>
                <li>Share your account credentials with others</li>
                <li>
                  Attempt to access accounts, systems, or data you are not
                  authorized to access
                </li>
                <li>
                  Interfere with, disrupt, or overload the Services or the
                  infrastructure that supports them
                </li>
                <li>
                  Use bots, scrapers, or automated tools to access or copy
                  content
                </li>
                <li>
                  Circumvent paywalls, access restrictions, or security
                  measures
                </li>
              </ul>
              <p>
                We may suspend or terminate your account if you violate these
                rules.
              </p>
            </section>

            {/* ── 8 ── */}
            <section className="terms-section">
              <h2>8. Third-Party Services</h2>
              <p>
                The Services may include links to or integrations with
                third-party websites and tools (for example, payment
                processors or video hosting providers). We are not responsible
                for the content, policies, or practices of those third
                parties. Your use of any third-party service is governed by
                that provider's own terms.
              </p>
            </section>

            {/* ── 9 ── */}
            <section className="terms-section">
              <h2>9. Disclaimers</h2>
              <p>
                THE SERVICES AND ALL COURSE CONTENT ARE PROVIDED "AS IS" AND
                "AS AVAILABLE." TO THE FULLEST EXTENT PERMITTED BY LAW, WE
                DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING
                WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR
                PURPOSE, AND NON-INFRINGEMENT.
              </p>
              <p>
                WE DO NOT WARRANT THAT THE SERVICES WILL BE UNINTERRUPTED OR
                ERROR-FREE, THAT CONTENT WILL BE ACCURATE OR COMPLETE, OR THAT
                USE OF THE SERVICES WILL PRODUCE ANY PARTICULAR EDUCATIONAL OR
                CAREER OUTCOME.
              </p>
            </section>

            {/* ── 10 ── */}
            <section className="terms-section">
              <h2>10. Limitation of Liability</h2>
              <p>
                TO THE FULLEST EXTENT PERMITTED BY LAW, HAYDUCATE AND ITS
                DIRECTORS, EMPLOYEES, AND AGENTS WILL NOT BE LIABLE FOR ANY
                INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE
                DAMAGES ARISING FROM OR RELATED TO YOUR USE OF THE SERVICES,
                INCLUDING LOSS OF PROFITS, DATA, OR GOODWILL.
              </p>
              <p>
                OUR TOTAL LIABILITY FOR ANY CLAIM ARISING FROM OR RELATING TO
                THE SERVICES WILL NOT EXCEED THE AMOUNT YOU PAID TO HAYDUCATE
                IN THE TWELVE MONTHS PRECEDING THE CLAIM.
              </p>
            </section>

            {/* ── 11 ── */}
            <section className="terms-section">
              <h2>11. Indemnity</h2>
              <p>
                You agree to indemnify and hold harmless Hayducate and its
                directors, employees, and agents from any claims, damages,
                losses, or expenses (including reasonable legal fees) arising
                from your violation of these Terms or your misuse of the
                Services.
              </p>
            </section>

            {/* ── 12 ── */}
            <section className="terms-section">
              <h2>12. Suspension and Termination</h2>
              <p>
                You may stop using the Services at any time. We may suspend or
                terminate your access if you violate these Terms, if we are
                required to by law, or if we discontinue the Services.
              </p>
              <p>
                Provisions that by their nature should survive termination —
                including intellectual property, disclaimers, limitation of
                liability, and indemnity — will continue to apply.
              </p>
            </section>

            {/* ── 13 ── */}
            <section className="terms-section">
              <h2>13. Governing Law</h2>
              <p>
                These Terms are governed by the laws of the Republic of Kenya,
                without regard to conflict-of-law principles. Any dispute
                arising from these Terms or your use of the Services will be
                subject to the exclusive jurisdiction of the courts of Kenya.
              </p>
            </section>

            {/* ── 14 ── */}
            <section className="terms-section">
              <h2>14. Changes to These Terms</h2>
              <p>
                We may update these Terms from time to time. When we make
                material changes, we will update the "Last updated" date at
                the top of this page and, where appropriate, notify you by
                email or in the Services. Continued use after changes means
                you accept the revised Terms.
              </p>
            </section>

            {/* ── 15 ── */}
            <section className="terms-section">
              <h2>15. Contact</h2>
              <p>
                If you have any questions about these Terms, please contact
                us:
              </p>
              <div className="terms-contact-info">
                <p>
                  <strong>Email:</strong>{" "}
                  <a
                    href={`mailto:${SITE.contact.email}`}
                    className="terms-link"
                  >
                    {SITE.contact.email}
                  </a>
                </p>
                <p>
                  <strong>Phone:</strong>{" "}
                  <a
                    href={`tel:${SITE.contact.phoneRaw}`}
                    className="terms-link"
                  >
                    {SITE.contact.phoneDisplay}
                  </a>
                </p>
                <p>
                  <strong>Location:</strong> {SITE.contact.location}
                </p>
                <p>
                  <strong>Contact form:</strong>{" "}
                  <Link to="/contact" className="terms-link">
                    Contact us
                  </Link>
                </p>
              </div>
            </section>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default TermsOfService;